import React, { useState, useEffect, useCallback } from 'react';
import { ContentRenderer } from './ContentRenderer';
import type { Playlist, PlaylistItem } from '@/hooks/signage';

interface PlaylistPlayerProps {
  playlist: Playlist;
}

export function PlaylistPlayer({ playlist }: PlaylistPlayerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [playOrder, setPlayOrder] = useState<number[]>([]);

  // Initialize play order based on loop mode
  useEffect(() => {
    if (!playlist.items.length) return;

    let order = playlist.items.map((_, i) => i);

    if (playlist.loop_mode === 'shuffle') {
      // Fisher-Yates shuffle
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
    }

    setPlayOrder(order);
    setCurrentIndex(0);
  }, [playlist.id, playlist.loop_mode, playlist.items.length]);

  const goToNext = useCallback(() => {
    if (!playlist.items.length) return;

    const nextIndex = currentIndex + 1;

    if (nextIndex >= playOrder.length) {
      if (playlist.loop_mode === 'once') {
        // Stay on last item
        return;
      }
      // Loop back to start (or reshuffle if shuffle mode)
      if (playlist.loop_mode === 'shuffle') {
        const newOrder = [...playOrder];
        for (let i = newOrder.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newOrder[i], newOrder[j]] = [newOrder[j], newOrder[i]];
        }
        setPlayOrder(newOrder);
      }
      setCurrentIndex(0);
    } else {
      setCurrentIndex(nextIndex);
    }
  }, [currentIndex, playOrder, playlist.loop_mode, playlist.items.length]);

  const handleContentComplete = useCallback(() => {
    // Start transition
    setIsTransitioning(true);

    setTimeout(() => {
      goToNext();
      setIsTransitioning(false);
    }, playlist.transition_duration_ms);
  }, [goToNext, playlist.transition_duration_ms]);

  if (!playlist.items.length) {
    return (
      <div className="w-full h-full bg-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Playlist is empty</p>
      </div>
    );
  }

  const currentItem = playlist.items[playOrder[currentIndex]];
  if (!currentItem) {
    return (
      <div className="w-full h-full bg-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  const getTransitionClass = () => {
    if (!isTransitioning) return 'opacity-100';

    switch (playlist.transition_type) {
      case 'fade':
        return 'opacity-0';
      case 'slide':
        return 'opacity-0 -translate-x-full';
      case 'zoom':
        return 'opacity-0 scale-110';
      default:
        return 'opacity-100';
    }
  };

  const duration = currentItem.duration_override || currentItem.content_item.duration_seconds;

  return (
    <div className="w-full h-full relative overflow-hidden">
      <div
        className={`w-full h-full transition-all ${getTransitionClass()}`}
        style={{
          transitionDuration: `${playlist.transition_duration_ms}ms`,
          transitionTimingFunction: 'ease-in-out',
        }}
      >
        <ContentRenderer
          key={`${currentItem.content_item.id}-${currentIndex}`}
          content={{
            ...currentItem.content_item,
            duration_seconds: duration,
          }}
          onComplete={handleContentComplete}
        />
      </div>

      {/* Debug info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded">
          {currentIndex + 1} / {playlist.items.length}
        </div>
      )}
    </div>
  );
}
